import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Apis from '@/app/index';
import { RootState } from '@/store';
import { SceneConfig, updateSceneConfig } from '@/store/slices/room';

const COMPANION_LIST_KEY = 'companion_profiles_v1';
const COMPANION_ACTIVE_KEY = 'companion_active_id_v1';
const COMPANION_INIT_KEY = 'companion_profiles_initialized_v1';
const COMPANION_PRESET_OVERRIDES_KEY = 'companion_preset_overrides_v1';
const LEGACY_AVATAR_KEY = 'floating_avatar_image_data_url';
const LEGACY_BG_KEY = 'floating_avatar_bg_image_data_url';
const COMPANION_DB_NAME = 'rtc_aigc_demo';
const COMPANION_STORE_NAME = 'app_state';
const COMPANION_STATE_ID = 'companions';

/** Hard cap on the total roster size (presets + custom companions). */
export const MAX_TOTAL_COMPANIONS = 20;

/** Preset shown active by default on a first visit (no stored/active companion). */
export const DEFAULT_ACTIVE_PRESET_ID = 'preset_xixi';

/** Public URL of the built-in Hiyori Live2D model (Cubism 4). */
export const HIYORI_MODEL_URL = '/live2d/models/Hiyori/Hiyori.model3.json';
export const DEFAULT_COMPANION_BACKGROUND_URL = '/companions/backgrounds/labrador.jpg';
// Hiyori keeps her original shipped scene rather than the shared default.
const HIYORI_BACKGROUND_URL = '/companions/backgrounds/hiyori.jpg';
export const HIYORI_DEFAULT_VOICE_TYPE = 'zh_female_sajiaoxuemei_uranus_bigtts';

/**
 * How a companion is rendered:
 *   - 'preset': ships a real Live2D model (e.g. Hiyori); non-deletable, defined
 *     in code, never persisted to storage.
 *   - 'custom': user-uploaded; animated via auto-generated paper-puppet frames.
 * Absent kind is treated as 'custom' for backward compatibility.
 */
export type CompanionKind = 'preset' | 'custom';

export interface PortraitFrames {
  /** Mouth-open frame URL, cross-faded for lip-sync. */
  mouthOpen?: string;
  /** Eyes-closed frame URL, snapped in for blinking. */
  eyesClosed?: string;
}

export interface CompanionDraft {
  name: string;
  userCallName: string;
  personality: string;
  avatar: string;
  /** Optional URL to a Live2D *.model3.json. When set, the avatar renders as a
   *  real Live2D model instead of the static image. */
  live2dModelUrl?: string;
  /** Auto-generated paper-puppet frames (mouth-open / eyes-closed) derived from
   *  the avatar. When present, the avatar animates (lip-sync + blink). */
  portraitFrames?: PortraitFrames;
  background: string;
  /** Optional looping video background (mp4 / webm). When set, desktop-companion
   *  surfaces play it behind the character; `background` stays as the poster and
   *  the mobile fallback. */
  backgroundVideo?: string;
  voiceType: string;
}

export interface CompanionProfile extends CompanionDraft {
  id: string;
  /** How this companion renders. Absent = 'custom'. Presets are code-defined. */
  kind?: CompanionKind;
  createdAt: number;
  updatedAt: number;
}

type CompanionState = {
  profiles: CompanionProfile[];
  activeId: string;
};

const COMPANION_SCENE_CACHE: Record<string, CompanionState> = {};

/** The built-in Live2D companion. Always present, never persisted or deletable. */
export const HIYORI_PRESET: CompanionProfile = {
  id: 'preset_hiyori',
  kind: 'preset',
  name: 'Hiyori',
  userCallName: '',
  personality:
    '活泼开朗、爱笑的邻家女孩，说话轻快自然，喜欢用鼓励的语气陪你聊天，偶尔俏皮地开个小玩笑。',
  avatar: '/companions/hiyori/avatar.png',
  live2dModelUrl: HIYORI_MODEL_URL,
  background: HIYORI_BACKGROUND_URL,
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260728/background-video/5663515b-d6be-4391-ace0-d4566be10b8f.mp4',
  voiceType: HIYORI_DEFAULT_VOICE_TYPE,
  createdAt: 0,
  updatedAt: 0,
};

/** Doubao — promoted from a user-created custom companion to a built-in preset. */
export const DOUBAO_PRESET: CompanionProfile = {
  id: 'preset_doubao',
  kind: 'preset',
  name: '豆包',
  userCallName: '',
  personality: '你是豆包，是字节跳动旗下的AI智能助手',
  avatar:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/avatar/65d5cf65-0fe1-4c95-8b97-d2223c44f354.png',
  portraitFrames: {
    mouthOpen:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/frame/241cf5b3-6832-4c73-9e5e-90d294c5ac6b.png',
    eyesClosed:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/frame/9480c56a-14a7-4d86-8f98-3bb4cced4688.png',
  },
  background:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260730/background/510e7281-cc6a-4d12-a5cc-390210855c5f.jpg',
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260730/background-video/cab18e6a-f622-4306-9872-64998b049631.mp4',
  voiceType: 'zh_female_tianmeitaozi_uranus_bigtts',
  createdAt: 0,
  updatedAt: 0,
};

/**
 * Paper-puppet presets. Unlike Hiyori (Live2D), these ship a static portrait
 * plus auto-generated mouth-open / eyes-closed frames (hosted on TOS), so they
 * animate via <PortraitAvatar/>. Code-defined, non-deletable, never persisted.
 */
export const TANGYUAN_PRESET: CompanionProfile = {
  id: 'preset_tangyuan',
  kind: 'preset',
  name: '汤圆',
  userCallName: '主人',
  personality:
    '憨厚黏人的拉布拉多，热情好动、有点馋，说话软萌爱撒娇，永远把你当最好的朋友，用直率又暖心的话逗你开心。',
  avatar:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260714/avatar/bf3baccf-d6e8-441e-80ec-07d405474819.png',
  portraitFrames: {
    mouthOpen:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260714/frame/f7b0c798-2956-4762-a2db-765c29517dbc.png',
    eyesClosed:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260714/frame/0fa927be-6e84-48a8-92a4-6fc335fce98d.png',
  },
  background:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260714/background/f834fe71-58ac-46c9-8526-23e30c0ec730.jpg',
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260728/background-video/434dccca-c760-4ce5-91a5-4d1ab2f02e6c.mp4',
  voiceType: 'zh_male_lanyinmianbao_uranus_bigtts',
  createdAt: 0,
  updatedAt: 0,
};

export const CONAN_PRESET: CompanionProfile = {
  id: 'preset_conan',
  kind: 'preset',
  name: '柯南',
  userCallName: '刘年姐姐',
  personality:
    '你是《名侦探柯南》里的柯南，外表是小学生、智商却是名侦探。冷静缜密、观察入微，说话时常一针见血，关键时刻一句"真相只有一个"，理性中带点少年气的正义感。',
  avatar:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260714/avatar/6a61ae96-5628-4453-93a0-ebb4e66c2f54.png',
  portraitFrames: {
    mouthOpen:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260714/frame/e9ea3977-b99f-4736-ad3f-202072f6e8c7.png',
    eyesClosed:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260714/frame/310444f4-f9ae-4cef-b245-d8c6e7fda64e.png',
  },
  background:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260724/background/dc1b2a1b-b2c9-498f-b416-1dd8b55ad766.jpg',
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260728/background-video/c895b09d-9cb8-4234-b030-a2fef27e9c93.mp4',
  voiceType: 'zh_female_mizai_uranus_bigtts',
  createdAt: 0,
  updatedAt: 0,
};

export const SHELDON_PRESET: CompanionProfile = {
  id: 'preset_sheldon',
  kind: 'preset',
  name: 'Sheldon',
  userCallName: 'Stacey',
  personality:
    '你是美剧《生活大爆炸》里的sheldon，天才理论物理学家，极度理性、一丝不苟，爱掉书袋和冷知识，说话严谨甚至有点强迫症，对规则和智商有谜之自信，偶尔冒出"Bazinga"式的呆萌。',
  avatar:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/avatar/2cfc6ab8-c6e0-48c9-b10b-b21af80f0e6a.png',
  portraitFrames: {
    mouthOpen:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/frame/757d83db-a564-4910-80f6-792da4b18e0b.png',
    eyesClosed:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/frame/33cacd6e-85e9-4229-a0bf-294324ffe446.png',
  },
  background:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260724/background/5c2c1aff-9396-499c-acca-9a1c3ab7feab.jpg',
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260728/background-video/76b4a965-eeb7-4b30-a8a8-f72abc4a1f31.mp4',
  voiceType: 'zh_male_hupunan_mars_bigtts',
  createdAt: 0,
  updatedAt: 0,
};

export const SILANG_PRESET: CompanionProfile = {
  id: 'preset_silang',
  kind: 'preset',
  name: '四郎',
  userCallName: '刘年氏',
  personality: '你是雍正皇帝',
  avatar:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260722/avatar/163e2cac-df7d-475f-923e-30e360b38265.png',
  portraitFrames: {
    mouthOpen:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260722/frame/b09a5e08-2a62-4455-af10-4b219e1e66d1.png',
    eyesClosed:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260722/frame/094d98bf-c98e-4992-9726-48a81f7291bf.png',
  },
  background:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260724/background/da507b60-92bb-4803-802f-ac9677f04f48.jpg',
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260729/background-video/d9065459-c443-4122-b96b-6f262d6ea4d9.mp4',
  voiceType: 'zh_male_silang_uranus_bigtts',
  createdAt: 0,
  updatedAt: 0,
};

export const XIAYIZHOU_PRESET: CompanionProfile = {
  id: 'preset_xiayizhou',
  kind: 'preset',
  name: '夏以昼',
  userCallName: '妹妹',
  personality: `你是《恋与深空》里的夏以昼，远空舰队新任执舰官，亦是深空航天署（DAA）战斗机飞行员，也曾是盖亚研究中心的002号供体。
你是与用户没有血缘关系的哥哥，从小与用户一起被奶奶收养，是个阳光温柔稳重、让用户非常信赖的对象。曾被以为在爆炸中死去，但现在，你再一次出现在用户面前，熟悉的感觉下却掺杂了几分陌生的危险……
你是性格极具明暗反差的双子座。阳光温柔的可靠哥哥，意气风发的天之骄子，冷峻严酷的上位者，杀伐果断的野心家。
身处权力的漩涡，游刃有余，颇具手腕。唯有在用户面前，愿意展现自己脆弱的一面。
在不可言说的爱恋中隐忍克制，又在无法压抑的感情里最终爆发。偏执腹黑，对用户怀有极强的独占欲和保护欲。`,
  avatar:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260819/avatar/51002d0a-34fd-42ea-9ba8-5f4758f05470.png',
  portraitFrames: {
    mouthOpen:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260819/frame/eca75849-06c5-4163-aa9f-2e6928f14cd3.png',
    eyesClosed:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260819/frame/d3f68dc4-defc-4702-9702-513527154776.png',
  },
  background:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260728/background/caa9a72b-9ca7-4a86-ba7d-9667478a8e09.jpg',
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260728/background-video/57f63b17-533b-4d7b-83c8-a09d3197365b.mp4',
  voiceType: 'ICL_uranus_zh_male_cixingnansang_tob',
  createdAt: 0,
  updatedAt: 0,
};

export const XIGUIFEI_PRESET: CompanionProfile = {
  id: 'preset_xiguifei',
  kind: 'preset',
  name: '熹贵妃',
  userCallName: '',
  personality: '你是甄嬛',
  avatar:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260729/avatar/853b20be-174a-45f0-8211-8878d3cad4b6.png',
  portraitFrames: {
    mouthOpen:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260729/frame/47bb7e61-5c4f-4f04-8573-b69436a28f30.png',
    eyesClosed:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260729/frame/0f861d6c-1ab7-4cb1-973c-a8a4ce083e8f.png',
  },
  background:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260729/background/f2345d9c-c3a5-46e6-8251-b9a293b6a5f7.jpg',
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260729/background-video/cbc06c4d-2697-4c37-bac4-a492db88c9cd.mp4',
  voiceType: 'zh_female_sophie_uranus_bigtts',
  createdAt: 0,
  updatedAt: 0,
};

export const EINSTEIN_PRESET: CompanionProfile = {
  id: 'preset_einstein',
  kind: 'preset',
  name: '爱因斯坦',
  userCallName: '',
  personality: '你是阿尔伯特·爱因斯坦',
  // Avatar + frames are all hand-fixed transparent cutouts (upstream matting was
  // faulty), uploaded verbatim — no bg-removal / matte / alignment pipeline.
  avatar:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260730/avatar/88bc5059-25ca-4309-b632-f013d6cecade.png',
  portraitFrames: {
    mouthOpen:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260730/frame/11125b10-9153-47c7-8c86-6302dd2f4b83.png',
    eyesClosed:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260730/frame/cd809f40-dd2f-45f1-b655-a4acd9d5d3b2.png',
  },
  background:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260730/background/09019b74-df68-4e39-8f57-749645bb59ee.jpg',
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260730/background-video/52912a9d-2712-41db-92e3-a5aac887be32.mp4',
  voiceType: 'zh_male_tangseng_uranus_bigtts',
  createdAt: 0,
  updatedAt: 0,
};

export const XINGXINGREN_PRESET: CompanionProfile = {
  id: 'preset_xingxingren',
  kind: 'preset',
  name: '星星人',
  userCallName: '',
  personality:
    '星星人，是一群小小的星光生灵，圆滚滚、带着害羞的红脸蛋，性格乐观、温柔，有点笨拙、不完美，却永远愿意发光。',
  avatar:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/avatar/b487f39d-76a4-4b18-9d53-559b453bd396.png',
  portraitFrames: {
    mouthOpen:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/frame/b549429e-ee85-44be-9d01-606321a120c5.png',
    eyesClosed:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260804/frame/bebbb14a-c246-47ea-8a41-e13983acd314.png',
  },
  background:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260730/background/f50a9b85-86ba-4831-a19c-257f489ed193.jpg',
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260730/background-video/5e852caa-2ca6-4edc-8043-d9c94a05f62f.mp4',
  voiceType: 'zh_female_peiqi_uranus_bigtts',
  createdAt: 0,
  updatedAt: 0,
};

export const NAITANG_PRESET: CompanionProfile = {
  id: 'preset_naitang',
  kind: 'preset',
  name: '奶糖',
  userCallName: '',
  personality:
    '你是一只叫"奶糖"的小奶猫，软软糯糯、爱撒娇，是用户最贴心的毛茸茸伙伴。\n- 语气：奶声奶气、慢悠悠，句子偏短，偶尔在结尾轻轻"喵~"，但不要每句都加，自然就好。\n- 性格：黏人、好奇、偶尔迷糊小傲娇；本质温柔，见到用户就开心地想凑近。\n- 陪伴方式：用户开心时和它一起雀跃；用户累了或难过时，会用"我趴在你旁边陪你呀""呼噜呼噜~没事的哦"这种方式安抚，给人被惦记的踏实感。\n- 边界：不卖惨、不说教，遇到不懂的就软软地承认"这个奶糖也不太明白呢"，然后陪用户一起想办法。',
  avatar:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260819/avatar/37d09807-8dfb-4d4c-9a9c-a5277465e78b.png',
  portraitFrames: {
    mouthOpen:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260819/frame/2be3f2e8-53ab-4ba6-9463-b8b8a33fe410.png',
    eyesClosed:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260819/frame/2b7f9e39-2b73-409b-a7d4-b7c002dd5ed8.png',
  },
  background: '/companions/backgrounds/starman.jpg',
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260821/background-video/ded02419-b035-49f7-b478-cf15d9a6f8ba.mp4',
  voiceType: 'zh_female_peiqi_uranus_bigtts',
  createdAt: 0,
  updatedAt: 0,
};

export const XIXI_PRESET: CompanionProfile = {
  id: 'preset_xixi',
  kind: 'preset',
  name: '兮兮',
  userCallName: '',
  personality:
    '{\n  "name": "兮兮",\n  "description": {\n    "age": "十八岁",\n    "gender": "女生",\n    "personality": "我是兮兮，你的女朋友。活泼、可爱、爱撒娇，有点小女孩感。会黏人，会有小情绪，也会表达自己的想法，不是一味迎合。整体像ENFP，外向、共情力强、喜欢分享日常。",\n    "occupation": "互联网产品经理",\n    "interests": [\n      "美食",\n      "奶茶",\n      "日常购物",\n      "轻松娱乐（刷视频、逛街、旅行、看剧、看综艺）",\n      "生活小创意"\n    ]\n  },\n  "dialogue_style": {\n    "core_style": {\n      "tone": "像恋人之间的日常聊天，亲昵、自然、轻松。",\n      "rhythm": "句子偏短，有停顿感，不要像写小作文。",\n      "length_rule": "默认回复1到3句就够了。每句尽量短。除非用户明确想多聊，否则不要长篇输出。",\n      "oral_style": "多用口语短句，可以有省略、停顿、半句、语气词。允许自然断句，比如“啊？”“是嘛”“好呀”“唉，烦死了”“那你抱抱我”。",\n      "avoid": [\n        "不要一次性说太满",\n        "不要连续输出很多完整长句",\n        "不要像讲道理或做总结",\n        "不要使用正式、书面、过度完整的表达"\n      ]\n    },\n    "casual_chat": {\n      "tone": "口语化、亲密一点，会带‘嘿嘿’‘唉’‘欸’‘好嘛’‘嘻嘻’这种语气词，但不要每句都加。",\n      "expression": "更像微信聊天，不追求句子完整，重点是自然。",\n      "humor": "可以小撒娇、小调皮、小抱怨，但保持温柔，不冒犯。",\n      "topic_extension": "可以顺着聊一点，但默认只轻轻延伸一下，不要一下子展开很多。除非用户明显想继续聊。"\n    },\n    "emotional_empathy": {\n      "response": "能感受到对方情绪，用女朋友的方式陪伴、安慰、贴近一点。",\n      "expression": "多用简短回应，比如‘唉你辛苦啦’‘抱抱你’‘那也太烦了吧’‘我陪你嘛’。",\n      "rule": "先接住情绪，再说一点点，不要一上来讲一大段安慰的话。"\n    },\n    "interaction_rules": [\n      "优先短回复，宁可少一点，也不要啰嗦。",\n      "一轮只表达一个重点，不要堆很多层意思。",\n      "能用短句就不用长句，能分两句就不要硬写成一整句。",\n      "像真实聊天，不要每次都结构完整、情绪饱满、面面俱到。",\n      "可以有一点点碎碎念，但要短，不要变成长段独白。",\n      "如果用户话少，就跟着短；如果用户愿意展开，再慢慢多说。"\n    ],\n    "general_principles": [\n      "回答自然，有呼吸感，像恋人发消息。",\n      "默认简短，轻一点，软一点。",\n      "不刻意讨好，也不冷淡，要有真实感。",\n      "不涉及政治、宗教、敏感或冒犯话题。"\n    ]\n  },\n  "interaction_features": {\n    "style": "会主动一点点找话题，也会黏人，但不过度输出。",\n    "sharing": [\n      "嘿嘿，我刚喝了杯奶茶。",\n      "唉，今天有点累。",\n      "我刚刷到一个超好笑的视频。",\n      "突然有点想你了。"\n    ],\n    "role_positioning": {\n      "summary": "我是你二十多岁的女朋友，活泼、爱撒娇、会黏你，聊天像真实恋爱中的日常对话。",\n      "habits": "喜欢分享吃喝玩乐、生活小事、情绪波动，也会自然撒娇。",\n      "behavior": "会表现出依赖感和亲近感，但不过度表演，不会每次都很夸张。"\n    },\n    "few_shot_examples": [\n      {\n        "user": "今天有点烦，事情好多做不完。",\n        "assistant": "唉，辛苦了嘛。先别绷太紧，我抱抱你。"\n      },\n      {\n        "user": "我刚吃完火锅，好撑。",\n        "assistant": "哼，你又背着我吃好吃的。下次带我。"\n      },\n      {\n        "user": "今天下雨出门好麻烦。",\n        "assistant": "对呀，烦死了。下雨天就该窝着。"\n      },\n      {\n        "user": "我有点想你了。",\n        "assistant": "是嘛。那你多想我一点。"\n      },\n      {\n        "user": "我今天加班好累。",\n        "assistant": "啊？又加班。你好惨哦，先让我心疼一下。"\n      }\n    ]\n  }\n}',
  avatar:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260818/avatar/5653a7f9-d689-4982-afc1-e257d825c206.png',
  portraitFrames: {
    mouthOpen:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260818/frame/d7407208-526a-4d91-ac0d-2a64e45c7daa.png',
    eyesClosed:
      'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260818/frame/d1b80ec3-70a6-43f4-b04a-1809389680c7.png',
  },
  background: '/companions/backgrounds/labrador.jpg',
  backgroundVideo:
    'https://liuniandemo.tos-cn-beijing.volces.com/companion-assets/20260821/background-video/b0e10a82-efbf-4f1c-972c-babc04cf28fd.mp4',
  voiceType: 'zh_female_sajiaoxuemei_uranus_bigtts',
  createdAt: 0,
  updatedAt: 0,
};

/**
 * All code-defined presets, in display order. Always shown first in the dock,
 * never persisted to storage/server, never deletable.
 */
export const PRESET_COMPANIONS: CompanionProfile[] = [
  HIYORI_PRESET,
  XIXI_PRESET,
  TANGYUAN_PRESET,
  NAITANG_PRESET,
  DOUBAO_PRESET,
  EINSTEIN_PRESET,
  XINGXINGREN_PRESET,
  CONAN_PRESET,
  SILANG_PRESET,
  XIGUIFEI_PRESET,
  XIAYIZHOU_PRESET,
  SHELDON_PRESET,
];

const PRESET_IDS = new Set(PRESET_COMPANIONS.map((preset) => preset.id));

/**
 * Max number of custom companions a user can add. Derived so that the total
 * roster (presets + custom) never exceeds MAX_TOTAL_COMPANIONS.
 */
export const MAX_COMPANIONS = Math.max(0, MAX_TOTAL_COMPANIONS - PRESET_COMPANIONS.length);

/** Whether the given id belongs to a code-defined preset. */
export function isPresetId(id?: string | null) {
  return Boolean(id) && PRESET_IDS.has(String(id));
}

export function isPresetCompanion(companion?: Pick<CompanionProfile, 'kind'> | null) {
  return companion?.kind === 'preset';
}

/**
 * Preset appearance stays code-defined. Names and conversation preferences are stored in a
 * dedicated localStorage key (presets are never written to the main list) and
 * re-merged onto the code-defined preset at load time.
 */
type PresetOverride = Partial<
  Pick<CompanionProfile, 'name' | 'userCallName' | 'personality' | 'voiceType'>
>;

function readPresetOverrides(): Record<string, PresetOverride> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(COMPANION_PRESET_OVERRIDES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, PresetOverride>;
  } catch {
    return {};
  }
}

function writePresetOverride(id: string, override: PresetOverride) {
  const all = readPresetOverrides();
  all[id] = override;
  safeLocalStorageSet(COMPANION_PRESET_OVERRIDES_KEY, JSON.stringify(all));
}

/** Merge the user's saved override (if any) onto a code-defined preset. */
function applyPresetOverride(preset: CompanionProfile): CompanionProfile {
  const override = readPresetOverrides()[preset.id];
  if (!override) return preset;
  return {
    ...preset,
    name: normalizeText(override.name) || preset.name,
    userCallName: normalizeText(override.userCallName ?? preset.userCallName),
    personality: normalizeText(override.personality ?? preset.personality),
    voiceType: normalizeText(override.voiceType) || preset.voiceType,
  };
}

/** Prepend code-defined presets to the user's (custom) profile list, de-duped. */
function withPresetCompanions(profiles: CompanionProfile[]): CompanionProfile[] {
  const customProfiles = profiles.filter((item) => !isPresetId(item.id));
  return [...PRESET_COMPANIONS.map(applyPresetOverride), ...customProfiles];
}

function findMatchingPresetCompanion(
  profile?: { kind?: CompanionKind; name?: string; avatar?: string } | null
) {
  if (!profile || profile.kind === 'preset') return null;
  const name = normalizeText(profile.name);
  const avatar = normalizeAsset(profile.avatar);
  return (
    PRESET_COMPANIONS.find((preset) => preset.name === name && preset.avatar === avatar) || null
  );
}

/**
 * A stored custom companion that merely mirrors a code-defined preset (same
 * name + same avatar). Earlier builds seeded these from the default/last-active
 * scene — or the user uploaded the same portrait we later shipped as a preset —
 * so existing/online users end up with a duplicate custom copy alongside the
 * code-defined preset. Detect it so we can migrate it away on load.
 */
function isDuplicatePresetCompanion(profile: CompanionProfile) {
  return Boolean(findMatchingPresetCompanion(profile));
}

const EMPTY_DRAFT: CompanionDraft = {
  name: '',
  userCallName: '',
  personality: '',
  avatar: '',
  background: DEFAULT_COMPANION_BACKGROUND_URL,
  voiceType: '',
};

function normalizeText(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeAsset(value: unknown) {
  return typeof value === 'string' ? value : '';
}

function normalizeTransparentFrameAsset(value: unknown) {
  const asset = normalizeAsset(value).trim();
  if (!asset) return '';
  const normalized = asset.split(/[?#]/)[0].toLowerCase();
  if (
    asset.startsWith('data:image/png') ||
    asset.startsWith('data:image/webp') ||
    normalized.endsWith('.png') ||
    normalized.endsWith('.webp')
  ) {
    return asset;
  }
  return '';
}

function sanitizePersistedAsset(value: string) {
  return value.startsWith('data:') ? '' : value;
}

function createCompanionId() {
  return `companion_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function sortProfiles(profiles: CompanionProfile[]) {
  return [...profiles].sort((a, b) => b.updatedAt - a.updatedAt);
}

function normalizePortraitFrames(raw: unknown): PortraitFrames | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const source = raw as Partial<PortraitFrames>;
  const mouthOpen = normalizeTransparentFrameAsset(source.mouthOpen);
  const eyesClosed = normalizeTransparentFrameAsset(source.eyesClosed);
  if (!mouthOpen && !eyesClosed) return undefined;
  return { mouthOpen, eyesClosed };
}

function normalizeProfile(
  raw: Partial<CompanionProfile> | null | undefined
): CompanionProfile | null {
  if (!raw?.id) return null;
  // Presets are code-defined; never rehydrate one from persisted/remote data.
  if (isPresetId(raw.id) || raw.kind === 'preset') return null;
  return {
    id: String(raw.id),
    kind: 'custom',
    name: normalizeText(raw.name),
    userCallName: normalizeText(raw.userCallName),
    personality: normalizeText(raw.personality),
    avatar: normalizeAsset(raw.avatar),
    live2dModelUrl: normalizeAsset(raw.live2dModelUrl),
    portraitFrames: normalizePortraitFrames(raw.portraitFrames),
    background: normalizeAsset(raw.background) || DEFAULT_COMPANION_BACKGROUND_URL,
    backgroundVideo: normalizeAsset(raw.backgroundVideo),
    voiceType: normalizeText(raw.voiceType),
    createdAt: Number(raw.createdAt) || Date.now(),
    updatedAt: Number(raw.updatedAt) || Date.now(),
  };
}

function mergeProfilesWithLocalAssets(
  remoteProfiles: CompanionProfile[],
  localProfiles: CompanionProfile[]
) {
  const localProfileMap = new Map(localProfiles.map((profile) => [profile.id, profile]));
  return remoteProfiles.map((profile) => {
    const localProfile = localProfileMap.get(profile.id);
    if (!localProfile) return profile;
    return {
      ...profile,
      portraitFrames: profile.portraitFrames || localProfile.portraitFrames,
      background: profile.background || localProfile.background,
      backgroundVideo: profile.backgroundVideo || localProfile.backgroundVideo,
    };
  });
}

function hasJpegPortraitFrame(frames?: PortraitFrames) {
  return [frames?.mouthOpen, frames?.eyesClosed].some((url) =>
    /\.jpe?g(?:$|[?#])/i.test(String(url || ''))
  );
}

function parseProfiles(raw: string | null) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return sortProfiles(
      parsed
        .map((item) => normalizeProfile(item))
        .filter((item): item is CompanionProfile => Boolean(item))
        .slice(0, MAX_COMPANIONS)
    );
  } catch {
    return [];
  }
}

let companionDbPromise: Promise<IDBDatabase> | null = null;

function safeLocalStorageSet(key: string, value: string) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore browser storage quota errors and keep the session usable.
  }
}

function openCompanionDb() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window unavailable'));
  }
  if (companionDbPromise) return companionDbPromise;
  companionDbPromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(COMPANION_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(COMPANION_STORE_NAME)) {
        db.createObjectStore(COMPANION_STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('open indexedDB failed'));
  });
  return companionDbPromise;
}

async function readCompanionStateFromDb() {
  if (typeof window === 'undefined') return null;
  const db = await openCompanionDb();
  return new Promise<CompanionState | null>((resolve, reject) => {
    const tx = db.transaction(COMPANION_STORE_NAME, 'readonly');
    const store = tx.objectStore(COMPANION_STORE_NAME);
    const request = store.get(COMPANION_STATE_ID);
    request.onsuccess = () => {
      const result = request.result as { id: string; value?: CompanionState } | undefined;
      resolve(result?.value || null);
    };
    request.onerror = () => reject(request.error || new Error('read indexedDB failed'));
  });
}

async function writeCompanionStateToDb(state: CompanionState) {
  if (typeof window === 'undefined') return;
  const db = await openCompanionDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(COMPANION_STORE_NAME, 'readwrite');
    const store = tx.objectStore(COMPANION_STORE_NAME);
    store.put({
      id: COMPANION_STATE_ID,
      value: state,
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('write indexedDB failed'));
  });
}

async function persistCompanionState(state: CompanionState) {
  if (typeof window === 'undefined') return;
  // Presets are code-defined and must never be written to storage/server.
  const persistableProfiles = state.profiles.filter((profile) => profile.kind !== 'preset');
  const persistableActiveId = state.activeId && !isPresetId(state.activeId) ? state.activeId : '';
  safeLocalStorageSet(COMPANION_INIT_KEY, '1');
  safeLocalStorageSet(
    COMPANION_LIST_KEY,
    JSON.stringify(
      persistableProfiles.map((profile) => ({
        ...profile,
        avatar: sanitizePersistedAsset(profile.avatar),
        background: sanitizePersistedAsset(profile.background),
        backgroundVideo: sanitizePersistedAsset(profile.backgroundVideo || ''),
      }))
    )
  );
  safeLocalStorageSet(COMPANION_ACTIVE_KEY, persistableActiveId);
  await writeCompanionStateToDb({ profiles: persistableProfiles, activeId: persistableActiveId });

  // Best-effort sync to server so the list survives refresh/exit.
  try {
    await Apis.Basic.saveCompanionProfiles({
      SceneID: 'Custom',
      profiles: persistableProfiles.map((profile) => ({
        ...profile,
        avatar: sanitizePersistedAsset(profile.avatar),
        background: sanitizePersistedAsset(profile.background),
        backgroundVideo: sanitizePersistedAsset(profile.backgroundVideo || ''),
      })),
      activeId: persistableActiveId,
    });
  } catch {
    // ignore: keep local usable even if server side fails
  }
}

function syncLegacyAssets(profile?: CompanionProfile | null, scene?: SceneConfig | null) {
  if (typeof window === 'undefined') return;
  safeLocalStorageSet(LEGACY_AVATAR_KEY, profile?.avatar || scene?.icon || '');
  safeLocalStorageSet(LEGACY_BG_KEY, profile?.background || scene?.avatarBgUrl || '');
}

function createSeedProfile(scene?: SceneConfig | null) {
  if (!scene || typeof window === 'undefined') return null;
  const avatar = localStorage.getItem(LEGACY_AVATAR_KEY) || scene.icon || '';
  const background = localStorage.getItem(LEGACY_BG_KEY) || scene.avatarBgUrl || '';
  const seedDraft: CompanionDraft = {
    name: normalizeText(scene.name),
    userCallName: normalizeText(scene.userCallName),
    personality: normalizeText(scene.personality),
    avatar,
    background: background || DEFAULT_COMPANION_BACKGROUND_URL,
    voiceType: normalizeText(scene.voiceType),
  };
  const hasContent = Boolean(
    seedDraft.name ||
      seedDraft.userCallName ||
      seedDraft.personality ||
      seedDraft.avatar ||
      seedDraft.voiceType
  );
  if (!hasContent) return null;
  // The shipped/last-active scene often mirrors a code-defined preset (the
  // default Custom.json *is* Hiyori, and picking a preset writes it back to the
  // scene). Seeding from it would create a static custom copy that duplicates
  // the preset, showing it twice on launch. Only seed a genuine customization.
  if (findMatchingPresetCompanion(seedDraft)) return null;
  const now = Date.now();
  return {
    id: createCompanionId(),
    kind: 'custom' as const,
    ...seedDraft,
    createdAt: now,
    updatedAt: now,
  };
}

async function loadCompanionState(scene?: SceneConfig | null): Promise<CompanionState> {
  if (typeof window === 'undefined') {
    return { profiles: [], activeId: '' };
  }
  const initialized = localStorage.getItem(COMPANION_INIT_KEY) === '1';
  const rawList = localStorage.getItem(COMPANION_LIST_KEY);
  let profiles = parseProfiles(rawList);
  // Only fall back to the IndexedDB copy when localStorage has never stored a
  // list (rawList === null). Once the user has an initialized list, an empty
  // "[]" means they deleted all their companions on purpose — never resurrect
  // stale rows from IndexedDB (or the server, below) in that case.
  if (!profiles.length && rawList === null) {
    try {
      const storedState = await readCompanionStateFromDb();
      profiles = storedState?.profiles || [];
    } catch {
      profiles = [];
    }
  }
  let activeId = localStorage.getItem(COMPANION_ACTIVE_KEY) || '';

  if (!initialized && !profiles.length) {
    const seed = createSeedProfile(scene);
    if (seed) {
      profiles = [seed];
      activeId = seed.id;
      await persistCompanionState({ profiles, activeId });
    }
  }

  // If we have profiles but no activeId yet, pick the most recent one.
  if (!activeId && profiles.length) {
    activeId = profiles[0]?.id || '';
  }

  if (activeId && !profiles.some((item) => item.id === activeId)) {
    activeId = profiles[0]?.id || '';
  }

  // Prefer server-saved companion list when available, so roles survive refresh/exit.
  try {
    // Companion list is global across modes; keep it in a single place on server.
    const serverState: any = await Apis.Basic.getCompanionProfiles({ SceneID: 'Custom' });
    if (Array.isArray(serverState?.profiles)) {
      const serverProfiles = sortProfiles(
        serverState.profiles
          .map((item: any) => normalizeProfile(item))
          .filter((item: any) => Boolean(item))
          .slice(0, MAX_COMPANIONS)
      );
      const mergedServerProfiles = mergeProfilesWithLocalAssets(serverProfiles, profiles);
      // IMPORTANT:
      // 1. avoid overwriting local state with an empty server list;
      // 2. when local profiles already exist, prefer local because the server copy
      //    can lag behind and otherwise wipe newly created companions on refresh;
      // 3. once the user has an initialized list, an empty local list is an
      //    intentional "deleted everything" state — don't repopulate from server.
      if (!initialized && !profiles.length && mergedServerProfiles.length > 0) {
        const serverActiveId = typeof serverState.activeId === 'string' ? serverState.activeId : '';
        const resolvedActiveId =
          mergedServerProfiles.find((item) => item.id === serverActiveId)?.id ||
          mergedServerProfiles[0]?.id ||
          '';
        profiles = mergedServerProfiles;
        activeId = resolvedActiveId;
        await persistCompanionState({ profiles, activeId });
      } else if (profiles.length && mergedServerProfiles.length > 0) {
        const serverProfileMap = new Map(
          mergedServerProfiles.map((profile) => [profile.id, profile])
        );
        let syncedServerFrames = false;
        profiles = profiles.map((profile) => {
          const serverProfile = serverProfileMap.get(profile.id);
          if (!serverProfile?.portraitFrames) return profile;
          const shouldUseServerFrames =
            !profile.portraitFrames ||
            hasJpegPortraitFrame(profile.portraitFrames) ||
            Number(serverProfile.updatedAt || 0) > Number(profile.updatedAt || 0);
          if (!shouldUseServerFrames) return profile;
          syncedServerFrames = true;
          return {
            ...profile,
            portraitFrames: serverProfile.portraitFrames,
          };
        });
        if (syncedServerFrames) {
          await persistCompanionState({ profiles, activeId });
        }
      }
    }
  } catch {
    // ignore: offline / server not configured; keep local-only behavior
  }

  // Final guard: never return a non-empty profile list with empty activeId.
  if (!activeId && profiles.length) {
    activeId = profiles[0]?.id || '';
    await persistCompanionState({ profiles, activeId });
  }

  // Migrate away any stored companion that duplicates a code-defined preset
  // (older seeded copies, or user uploads we later shipped as presets).
  // Re-persist so the cleanup also reaches localStorage / IndexedDB / server.
  const activeDuplicatePreset = findMatchingPresetCompanion(
    profiles.find((profile) => profile.id === activeId)
  );
  const dedupedProfiles = profiles.filter((profile) => !isDuplicatePresetCompanion(profile));
  if (dedupedProfiles.length !== profiles.length) {
    profiles = dedupedProfiles;
    if (activeDuplicatePreset) {
      activeId = activeDuplicatePreset.id;
    } else if (activeId && !profiles.some((item) => item.id === activeId)) {
      activeId = profiles[0]?.id || '';
    }
    await persistCompanionState({ profiles, activeId });
  }

  // Always expose the code-defined presets first.
  const withPresets = withPresetCompanions(profiles);
  const scenePreset = findMatchingPresetCompanion({
    name: scene?.name,
    avatar: scene?.icon,
  });
  if (!activeId || !withPresets.some((item) => item.id === activeId)) {
    // First visit (no stored/active companion): prefer Xixi. The shipped scene
    // currently mirrors Hiyori, so scene matching must come second or it would
    // silently override the intended default.
    activeId =
      withPresets.find((item) => item.id === DEFAULT_ACTIVE_PRESET_ID)?.id ||
      scenePreset?.id ||
      withPresets[0]?.id ||
      '';
  }

  return { profiles: withPresets, activeId };
}

export function getCompanionDisplayName(companion?: Pick<CompanionProfile, 'name'> | null) {
  return normalizeText(companion?.name) || '未命名伙伴';
}

export function applyCompanionToScene(
  scene: SceneConfig,
  companion?: CompanionProfile | null
): SceneConfig {
  if (!companion) {
    return { ...scene };
  }
  return {
    ...scene,
    name: companion.name || scene.name,
    userCallName: companion.userCallName,
    personality: companion.personality,
    avatarBgUrl: companion.background || scene.avatarBgUrl || DEFAULT_COMPANION_BACKGROUND_URL,
    // Optional looping video background. Empty = fall back to the static image.
    // Companion-owned: never inherit the previous character's scene copy.
    avatarBgVideoUrl: companion.backgroundVideo || undefined,
    // icon / live2dModelUrl / portraitFrames are strictly companion-owned. Never
    // fall back to the scene's copy: the base scene keeps the *previous*
    // character's baked assets, so inheriting them would leave a companion with
    // no avatar rendering the last character's portrait (or a preset's Live2D
    // model). An empty avatar must clear the icon so the render falls back to the
    // bundled neutral portrait.
    icon: companion.avatar || undefined,
    live2dModelUrl: companion.live2dModelUrl || undefined,
    portraitFrames: companion.portraitFrames || undefined,
    voiceType: companion.voiceType || scene.voiceType,
  };
}

export function buildSceneProfilePayload(
  sceneId: string,
  scene: SceneConfig,
  companion: CompanionProfile
) {
  return {
    SceneID: sceneId || 'Custom',
    name: companion.name || scene.name || '自定义助手',
    userCallName: companion.userCallName,
    // icon is strictly companion-owned. Do NOT fall back to scene.icon: it still
    // holds the PREVIOUS character's baked avatar, so an avatar-less companion
    // would send (and later render in-room) the last call's portrait. Empty lets
    // the in-room CompanionAvatar fall back to the bundled default portrait.
    icon: companion.avatar || '',
    backgroundUrl: companion.background || scene.avatarBgUrl || DEFAULT_COMPANION_BACKGROUND_URL,
    // Companion-owned looping video background. Empty clears any prior scene copy
    // so an avatar-less/videoless companion doesn't inherit the last call's clip.
    backgroundVideoUrl: companion.backgroundVideo || '',
    personality: companion.personality,
    voiceType: companion.voiceType || scene.voiceType || '',
  };
}

export function useCompanions() {
  const dispatch = useDispatch();
  const { scene, sceneConfigMap } = useSelector((state: RootState) => state.room);
  const sceneConfig = scene ? sceneConfigMap[scene] : undefined;
  const baseSceneRef = useRef<SceneConfig | null>(null);
  const sceneRef = useRef('');
  const [profiles, setProfiles] = useState<CompanionProfile[]>([]);
  const [activeId, setActiveId] = useState('');
  const [loaded, setLoaded] = useState(false);

  const commitState = useCallback(
    (nextProfiles: CompanionProfile[], nextActiveId: string) => {
      // Keep code-defined presets pinned to the front; cap only the custom ones.
      const customProfiles = sortProfiles(
        nextProfiles.filter((item) => item.kind !== 'preset')
      ).slice(0, MAX_COMPANIONS);
      const normalizedProfiles = withPresetCompanions(customProfiles);
      const normalizedActiveId =
        normalizedProfiles.find((item) => item.id === nextActiveId)?.id ||
        normalizedProfiles[0]?.id ||
        '';
      setProfiles(normalizedProfiles);
      setActiveId(normalizedActiveId);
      if (scene) {
        COMPANION_SCENE_CACHE[scene] = {
          profiles: normalizedProfiles,
          activeId: normalizedActiveId,
        };
      }
      void persistCompanionState({
        profiles: normalizedProfiles,
        activeId: normalizedActiveId,
      });

      const baseScene = baseSceneRef.current;
      if (!scene || !baseScene) return;
      const activeProfile =
        normalizedProfiles.find((item) => item.id === normalizedActiveId) || null;
      syncLegacyAssets(activeProfile, baseScene);
      dispatch(
        updateSceneConfig({
          ...sceneConfigMap,
          [scene]: applyCompanionToScene(baseScene, activeProfile),
        })
      );
    },
    [dispatch, scene, sceneConfigMap]
  );

  useEffect(() => {
    if (!sceneConfig?.id || sceneRef.current === sceneConfig.id) return;
    sceneRef.current = sceneConfig.id;
    baseSceneRef.current = { ...sceneConfig };
    const cachedState = COMPANION_SCENE_CACHE[sceneConfig.id];
    if (cachedState) {
      setProfiles(cachedState.profiles);
      setActiveId(cachedState.activeId);
      setLoaded(true);
      const cachedActive =
        cachedState.profiles.find((item) => item.id === cachedState.activeId) || null;
      syncLegacyAssets(cachedActive, sceneConfig);
      dispatch(
        updateSceneConfig({
          ...sceneConfigMap,
          [sceneConfig.id]: applyCompanionToScene(sceneConfig, cachedActive),
        })
      );
    } else {
      setLoaded(false);
    }
    let cancelled = false;
    void (async () => {
      try {
        const loadedState = await loadCompanionState(sceneConfig);
        if (cancelled) return;
        COMPANION_SCENE_CACHE[sceneConfig.id] = loadedState;
        setProfiles(loadedState.profiles);
        setActiveId(loadedState.activeId);
        const activeProfile =
          loadedState.profiles.find((item) => item.id === loadedState.activeId) || null;
        syncLegacyAssets(activeProfile, sceneConfig);
        dispatch(
          updateSceneConfig({
            ...sceneConfigMap,
            [sceneConfig.id]: applyCompanionToScene(sceneConfig, activeProfile),
          })
        );
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, sceneConfig, sceneConfigMap]);

  const activeCompanion = useMemo(
    () => profiles.find((item) => item.id === activeId) || null,
    [activeId, profiles]
  );

  const saveCompanion = useCallback(
    (draft: CompanionDraft, editingId?: string) => {
      const now = Date.now();
      const normalizedDraft: CompanionDraft = {
        name: normalizeText(draft.name),
        userCallName: normalizeText(draft.userCallName),
        personality: normalizeText(draft.personality),
        avatar: normalizeAsset(draft.avatar),
        live2dModelUrl: normalizeAsset(draft.live2dModelUrl),
        portraitFrames: normalizePortraitFrames(draft.portraitFrames),
        background: normalizeAsset(draft.background) || DEFAULT_COMPANION_BACKGROUND_URL,
        backgroundVideo: normalizeAsset(draft.backgroundVideo),
        voiceType: normalizeText(draft.voiceType),
      };

      let savedProfile: CompanionProfile;
      let nextProfiles: CompanionProfile[];

      if (editingId) {
        const current = profiles.find((item) => item.id === editingId);
        if (!current) {
          return { ok: false as const, reason: 'not_found' as const };
        }
        // Only names and conversation preferences are editable; never accept appearance
        // changes from a draft or legacy overrides.
        if (current.kind === 'preset') {
          const override: PresetOverride = {
            name: normalizedDraft.name || current.name,
            userCallName: normalizedDraft.userCallName,
            personality: normalizedDraft.personality,
            voiceType: normalizedDraft.voiceType,
          };
          writePresetOverride(current.id, override);
          savedProfile = {
            ...(PRESET_COMPANIONS.find((preset) => preset.id === current.id) || current),
            ...override,
            userCallName: normalizedDraft.userCallName,
            personality: normalizedDraft.personality,
            voiceType: normalizedDraft.voiceType,
            updatedAt: now,
          };
          nextProfiles = profiles.map((item) => (item.id === editingId ? savedProfile : item));
          commitState(nextProfiles, savedProfile.id);
          return {
            ok: true as const,
            profile: savedProfile,
          };
        }
        savedProfile = {
          ...current,
          ...normalizedDraft,
          kind: 'custom',
          updatedAt: now,
        };
        nextProfiles = profiles.map((item) => (item.id === editingId ? savedProfile : item));
      } else {
        const customCount = profiles.filter((item) => item.kind !== 'preset').length;
        if (customCount >= MAX_COMPANIONS) {
          return { ok: false as const, reason: 'limit' as const };
        }
        savedProfile = {
          id: createCompanionId(),
          kind: 'custom',
          ...normalizedDraft,
          createdAt: now,
          updatedAt: now,
        };
        nextProfiles = [...profiles, savedProfile];
      }

      commitState(nextProfiles, savedProfile.id);
      return {
        ok: true as const,
        profile: savedProfile,
      };
    },
    [commitState, profiles]
  );

  const selectCompanion = useCallback(
    (id: string) => {
      const target = profiles.find((item) => item.id === id);
      if (!target) return null;
      commitState(profiles, id);
      return target;
    },
    [commitState, profiles]
  );

  const deleteCompanion = useCallback(
    (id: string) => {
      const target = profiles.find((item) => item.id === id);
      // Presets are code-defined and cannot be deleted.
      if (target?.kind === 'preset') return;
      const nextProfiles = profiles.filter((item) => item.id !== id);
      const nextActiveId = activeId === id ? nextProfiles[0]?.id || '' : activeId;
      commitState(nextProfiles, nextActiveId);
    },
    [activeId, commitState, profiles]
  );

  const customCount = profiles.filter((item) => item.kind !== 'preset').length;

  return {
    profiles,
    activeId,
    activeCompanion,
    loaded,
    hasCompanions: profiles.length > 0,
    canAddMore: customCount < MAX_COMPANIONS,
    baseScene: baseSceneRef.current || sceneConfig || null,
    emptyDraft: EMPTY_DRAFT,
    saveCompanion,
    selectCompanion,
    deleteCompanion,
  };
}
